import { BufferAttribute, BufferGeometry, Color, FrontSide, Group, Mesh, MeshLambertMaterial } from 'three';
import type { Engine } from '../engine';
import type { EngineObject } from '../engine.types';
import { PartType } from '../../models/enums';
import type { TriangleSoup } from '../design-region-bounds';
import { overlapSolids, OverlapSolid, OverlapSolidsMessage, OverlapSolidsRequest } from '../design-overlap-solids';
import { regionVolumes } from '../../services/region-volumes.util';

// Apart from the orange design parts, the grey non-design parts and the
// red sharp-edge overlay.
const OVERLAP_COLOR = new Color('#c026d3');
const OVERLAP_OPACITY = 0.55;

/** A part's faces as non-indexed triangles, in the frame its group draws in. */
function partSoup(object: EngineObject): TriangleSoup {
  let count = 0;
  for (const face of object.data.faces) count += face.indices.length;
  const soup = new Float32Array(count * 3);
  let offset = 0;
  for (const { vertices, indices } of object.data.faces) {
    for (let i = 0; i < indices.length; i++) {
      const at = indices[i] * 3;
      soup[offset++] = vertices[at];
      soup[offset++] = vertices[at + 1];
      soup[offset++] = vertices[at + 2];
    }
  }
  return soup;
}

/**
 * Admin overlay: where non-design parts cover the design parts. The
 * optimizer gives that volume to the non-design part, so it is left out of
 * the design mass. Drawn as the solid the parts share, on top of everything,
 * under each design part's group; the solids are cut in a worker.
 */
export class DesignOverlapManager {
  private enabled = false;
  private shown = false;
  private hasOverlap = false;
  private refreshQueued = false;
  private worker: Worker | null = null;
  private job = 0;
  private fraction: number | null = null;
  private readonly holders = new Map<string, Group>();

  /** Called whenever {@link available} changes. */
  onAvailabilityChange: ((available: boolean) => void) | null = null;
  /** Called whenever {@link progress} changes. */
  onProgress: ((progress: number | null) => void) | null = null;

  constructor(private readonly engine: Engine) {}

  get visible(): boolean {
    return this.shown;
  }

  /** Whether some design part lies partly inside a non-design part. */
  get available(): boolean {
    return this.hasOverlap;
  }

  /** How far the overlay is built, 0 to 1; null when it is not being built. */
  get progress(): number | null {
    return this.fraction;
  }

  /** Starts tracking availability; nothing is measured before this, so only admins pay for it. */
  enable(): void {
    this.enabled = true;
    this.refresh();
  }

  show(): void {
    this.shown = true;
    this.rebuild();
  }

  hide(): void {
    this.shown = false;
    this.cancel();
    this.clear();
    this.engine.requestRender();
  }

  /** Re-checks availability and the overlay against the current parts, once per tick. */
  refresh(): void {
    if (!this.enabled || this.refreshQueued) return;
    this.refreshQueued = true;
    queueMicrotask(() => {
      this.refreshQueued = false;
      this.update();
    });
  }

  private update(): void {
    const available = regionVolumes(this.engine.objects).overlapVolume > 0;
    if (available !== this.hasOverlap) {
      this.hasOverlap = available;
      // Nothing left to show: the overlay goes off with the switch.
      if (!available && this.shown) this.hide();
      this.onAvailabilityChange?.(available);
    }
    if (this.shown) this.rebuild();
  }

  /** Any part changing type changes every design part's overlap, so it is all cut again. */
  private rebuild(): void {
    this.cancel();
    this.clear();
    const design = this.engine.objects.filter((o) => o.type === PartType.DESIGN);
    const nondesign = this.engine.objects.filter((o) => o.type === PartType.NONDESIGN);
    if (design.length === 0 || nondesign.length === 0) {
      this.engine.requestRender();
      return;
    }
    const request: OverlapSolidsRequest = {
      design: design.map((object) => ({ id: String(object.data.id), soup: partSoup(object) })),
      nondesign: nondesign.map(partSoup)
    };

    if (typeof Worker === 'undefined') {
      this.draw(overlapSolids(request));
      return;
    }
    const job = ++this.job;
    const worker = new Worker(new URL('./design-overlap.worker', import.meta.url), { type: 'module' });
    this.worker = worker;
    this.setProgress(0);
    worker.onmessage = (event: MessageEvent<OverlapSolidsMessage>) => {
      if (job !== this.job) return;
      const message = event.data;
      if (message.type === 'progress') {
        this.setProgress(message.total === 0 ? 1 : message.done / message.total);
        return;
      }
      this.cancel();
      if (message.type === 'done') this.draw(message.solids);
      else console.warn('Design overlap:', message.message);
    };
    worker.onerror = (error) => {
      if (job !== this.job) return;
      this.cancel();
      console.warn('Design overlap:', error.message);
    };
    const transfer = [...request.design.map((part) => part.soup.buffer), ...request.nondesign.map((soup) => soup.buffer)];
    worker.postMessage(request, transfer);
  }

  /** Stops the build in progress, if any; its results are dropped. */
  private cancel(): void {
    this.job++;
    this.worker?.terminate();
    this.worker = null;
    this.setProgress(null);
  }

  private setProgress(progress: number | null): void {
    if (progress === this.fraction) return;
    this.fraction = progress;
    this.onProgress?.(progress);
  }

  private draw(solids: readonly OverlapSolid[]): void {
    for (const { id, positions } of solids) {
      const object = this.engine.objects.find((o) => String(o.data.id) === id && o.type === PartType.DESIGN);
      if (!object) continue;
      const holder = this.buildHolder(positions);
      object.group.add(holder);
      this.holders.set(id, holder);
    }
    this.engine.requestRender();
  }

  private clear(): void {
    for (const holder of this.holders.values()) {
      holder.removeFromParent();
      holder.traverse((child) => {
        if (child instanceof Mesh) {
          child.geometry.dispose();
          (child.material as MeshLambertMaterial).dispose();
        }
      });
    }
    this.holders.clear();
  }

  // Wrapped in a group, as the sharp-edge overlay is: the hover outline takes
  // every Mesh directly under the part's group.
  private buildHolder(positions: Float32Array): Group {
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    const material = new MeshLambertMaterial({
      color: OVERLAP_COLOR,
      transparent: true,
      opacity: OVERLAP_OPACITY,
      // The overlap sits inside both parts, so it is drawn through them; front
      // faces only, so the far side of the solid is not laid over the near.
      side: FrontSide,
      depthTest: false,
      depthWrite: false,
      flatShading: true
    });
    const mesh = new Mesh(geometry, material);
    mesh.name = 'design-overlap';
    mesh.renderOrder = 10;
    // Not pickable: clicks go to the part underneath.
    mesh.raycast = () => {};

    const holder = new Group();
    holder.name = 'design-overlap';
    holder.add(mesh);
    return holder;
  }
}
